import { ReactNode } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ShieldCheck, Users2, Building2, ClipboardList, ScrollText, Layers,
  AlertTriangle, Mail,
} from 'lucide-react';
import { cn } from '../lib/cn';
import { PlyEZMark } from './BrandLogo';
import { LanguagePicker } from './LanguagePicker';
import { UserMenu } from './UserMenu';

const NAV = [
  { to: '/admin',            icon: ShieldCheck,    key: 'overview' },
  { to: '/admin/users',      icon: Users2,         key: 'users' },
  { to: '/admin/workspaces', icon: Building2,      key: 'workspaces' },
  { to: '/admin/queue',      icon: ClipboardList,  key: 'queue' },
  { to: '/admin/jobs',       icon: Layers,         key: 'jobs' },
  { to: '/admin/incidents',  icon: AlertTriangle,  key: 'incidents' },
  { to: '/admin/email-log',  icon: Mail,           key: 'emailLog' },
  { to: '/admin/audit',      icon: ScrollText,     key: 'audit' },
] as const;

export function AdminShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen bg-ink-50 text-ink-900 dark:bg-ink-950 dark:text-ink-100">
      <aside className="w-60 shrink-0 border-r border-ink-200 dark:border-ink-800 bg-white dark:bg-ink-900 flex flex-col">
        <div className="px-5 py-5 border-b border-ink-200 dark:border-ink-800">
          <Link to="/" className="flex items-center gap-2">
            <PlyEZMark size={24} />
            <span className="font-display text-lg font-semibold">{t('brand')}</span>
          </Link>
          <div className="text-[11px] uppercase tracking-[0.18em] text-ink-500 mt-1">
            {t('admin.shellSubtitle')}
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-2">
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.key}
                to={item.to}
                end={item.to === '/admin'}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-5 py-2 text-sm transition-colors',
                    isActive
                      ? 'bg-accent-50 text-accent-700 dark:bg-accent-900/40 dark:text-accent-100 border-r-2 border-accent-500'
                      : 'text-ink-600 hover:bg-ink-50 hover:text-ink-900 dark:text-ink-300 dark:hover:bg-ink-800',
                  )
                }
              >
                <Icon size={16} />
                <span>{t(`admin.nav.${item.key}`)}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="px-5 py-4 border-t border-ink-200 dark:border-ink-800">
          <LanguagePicker compact />
        </div>
      </aside>
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 px-6 border-b border-ink-200 dark:border-ink-800 bg-white dark:bg-ink-900 flex items-center justify-between">
          <div className="text-sm text-ink-500">{t('admin.title')}</div>
          <UserMenu />
        </header>
        <main className="flex-1 overflow-auto">{children}</main>
      </div>
    </div>
  );
}
